import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import * as React from 'react';
import { throttle } from 'throttle-debounce';
export function useSliderContextBuilder() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      loop = _ref.loop;

  var scroller = React.useRef(null);
  var mutationObserverRef = React.useRef(null);
  var currentSlideRef = React.useRef(0);
  var totalSlidesRef = React.useRef(0);

  var _React$useState = React.useState(0),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      currentSlide = _React$useState2[0],
      setCurrentSlide = _React$useState2[1];

  var _React$useState3 = React.useState(0),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      totalSlides = _React$useState4[0],
      setTotalSlides = _React$useState4[1];

  var scrollTo = React.useCallback(function (index, options) {
    var _scroller$current;

    (_scroller$current = scroller.current) === null || _scroller$current === void 0 ? void 0 : _scroller$current.scrollBy(_objectSpread({
      left: (index - currentSlideRef.current) * scroller.current.clientWidth,
      behavior: 'smooth'
    }, options));
  }, []);
  var scrollPrev = React.useCallback(function () {
    if (currentSlideRef.current > 0) {
      var _scroller$current2;

      (_scroller$current2 = scroller.current) === null || _scroller$current2 === void 0 ? void 0 : _scroller$current2.scrollBy({
        left: -scroller.current.clientWidth,
        behavior: 'smooth'
      });
    } else if (loop) {
      scrollTo(totalSlidesRef.current - 1);
    }
  }, [loop, scrollTo]);
  var scrollNext = React.useCallback(function () {
    if (currentSlideRef.current + 1 < totalSlidesRef.current) {
      var _scroller$current3;

      (_scroller$current3 = scroller.current) === null || _scroller$current3 === void 0 ? void 0 : _scroller$current3.scrollBy({
        left: scroller.current.clientWidth,
        behavior: 'smooth'
      });
    } else if (loop) {
      scrollTo(0);
    }
  }, [loop, scrollTo]);
  var calculateTotalSlides = React.useCallback(function () {
    if (scroller.current) {
      totalSlidesRef.current = Math.round(scroller.current.scrollWidth / scroller.current.clientWidth);
      setTotalSlides(totalSlidesRef.current);
    }
  }, []);
  var handleScroll = React.useCallback(throttle(50, function () {
    if (scroller.current) {
      currentSlideRef.current = Math.round(scroller.current.scrollLeft / scroller.current.clientWidth);
      setCurrentSlide(currentSlideRef.current);
    }
  }), []);
  var scrollerRef = React.useCallback(function (node) {
    if (!mutationObserverRef.current) {
      mutationObserverRef.current = new MutationObserver(throttle(50, calculateTotalSlides));
    }

    if (scroller.current) {
      var _mutationObserverRef$;

      (_mutationObserverRef$ = mutationObserverRef.current) === null || _mutationObserverRef$ === void 0 ? void 0 : _mutationObserverRef$.disconnect();
      scroller.current.removeEventListener('scroll', handleScroll);
    }

    if (node) {
      var _mutationObserverRef$2;

      (_mutationObserverRef$2 = mutationObserverRef.current) === null || _mutationObserverRef$2 === void 0 ? void 0 : _mutationObserverRef$2.observe(node, {
        subtree: true,
        childList: true
      });
      node.addEventListener('scroll', handleScroll, {
        passive: true
      });
    }

    scroller.current = node;
  }, [calculateTotalSlides, handleScroll]);
  useIsomorphicLayoutEffect(function () {
    calculateTotalSlides();
  }, []);
  return React.useMemo(function () {
    return {
      scrollerRef: scrollerRef,
      currentSlide: currentSlide,
      totalSlides: totalSlides,
      scrollTo: scrollTo,
      scrollPrev: scrollPrev,
      scrollNext: scrollNext
    };
  }, [currentSlide, scrollNext, scrollPrev, scrollTo, scrollerRef, totalSlides]);
}